import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getSectors, deleteSector } from '../../actions/sector';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { Plus, Search, Edit, Trash2, MapPin, Eye, AlertTriangle, Activity, Layers, Building, Clock } from 'lucide-react';

const Sectors = ({ 
    getSectors, 
    deleteSector, 
    getServiceableAreas,
    sectors, 
    serviceableAreas,
    loading 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [sectorToDelete, setSectorToDelete] = useState(null);
    const [filteredSectors, setFilteredSectors] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [serviceableAreaFilter, setServiceableAreaFilter] = useState('all');

    useEffect(() => {
        document.title = 'Sectors - Management';
        getSectors();
        getServiceableAreas(); // Load serviceable areas for filtering
    }, [getSectors, getServiceableAreas]);

    useEffect(() => {
        if (sectors) {
            let filtered = sectors.filter(sector =>
                sector.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sector.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sector.serviceableArea?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sector.microWarehouse?.address?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (statusFilter !== 'all') {
                filtered = filtered.filter(sector =>
                    statusFilter === 'active' ? sector.isActive : !sector.isActive
                );
            }

            if (serviceableAreaFilter !== 'all') {
                filtered = filtered.filter(sector =>
                    sector.serviceableArea?._id === serviceableAreaFilter
                );
            }

            setFilteredSectors(filtered);
        }
    }, [sectors, searchTerm, statusFilter, serviceableAreaFilter]);

    const handleDeleteClick = (sector) => {
        setSectorToDelete(sector);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (sectorToDelete) {
            deleteSector(sectorToDelete._id);
            setShowDeleteModal(false);
            setSectorToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setSectorToDelete(null);
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'secondary';
    };

    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };

    if (loading && !sectors) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading sectors...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary mb-1 fw-bold">
                                    <Layers className="me-2" size={32} />
                                    Sectors
                                </h2>
                                <p className="text-muted mb-0">Manage operational sectors within serviceable areas</p>
                            </div>
                            <Link
                                to="/inventory/v1.2/sectors/new"
                                className="btn btn-primary btn-lg d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add Sector
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-4">
                    <div className="col-md-6">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <Search size={18} />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, description, area, or warehouse address..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={serviceableAreaFilter}
                            onChange={(e) => setServiceableAreaFilter(e.target.value)}
                        >
                            <option value="all">All Serviceable Areas</option>
                            {serviceableAreas?.map(area => (
                                <option key={area._id} value={area._id}>
                                    {area.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Total Sectors</h5>
                                        <h3 className="mt-2 mb-0">{sectors?.length || 0}</h3>
                                    </div>
                                    <Layers size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Active Sectors</h5>
                                        <h3 className="mt-2 mb-0">
                                            {sectors?.filter(sector => sector.isActive).length || 0}
                                        </h3>
                                    </div>
                                    <Activity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Warehouses</h5>
                                        <h3 className="mt-2 mb-0">
                                            {sectors?.filter(sector => sector.microWarehouse).length || 0}
                                        </h3>
                                    </div>
                                    <Building size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Filtered Results</h5>
                                        <h3 className="mt-2 mb-0">{filteredSectors?.length || 0}</h3>
                                    </div>
                                    <Search size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Sectors List</h5>
                    </div>
                    <div className="card-body p-0">
                        {filteredSectors && filteredSectors.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Sector Name</th>
                                            <th>Serviceable Area</th>
                                            <th>Micro Warehouse</th>
                                            <th>Max Drive Time</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSectors.map((sector) => (
                                            <tr key={sector._id}>
                                                <td>
                                                    <div className="fw-bold text-primary">{sector.name}</div>
                                                    {sector.description && (
                                                        <small className="text-muted">
                                                            {sector.description.length > 40 
                                                                ? `${sector.description.substring(0, 40)}...`
                                                                : sector.description
                                                            }
                                                        </small>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <MapPin size={16} className="text-muted me-2" />
                                                        <div>
                                                            <div className="fw-semibold">
                                                                {sector.serviceableArea?.name || 'Not Assigned'}
                                                            </div>
                                                            {sector.serviceableArea && (
                                                                <small className="text-muted">
                                                                    {sector.serviceableArea.city}, {sector.serviceableArea.state}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {sector.microWarehouse ? (
                                                        <div className="d-flex align-items-center">
                                                            <Building size={16} className="text-muted me-2" />
                                                            <div>
                                                                <div className="fw-semibold">
                                                                    {sector.microWarehouse.contactPerson || 'Warehouse'}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {sector.microWarehouse.address.length > 30
                                                                        ? `${sector.microWarehouse.address.substring(0, 30)}...`
                                                                        : sector.microWarehouse.address
                                                                    }
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <em className="text-muted">Not configured</em>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Clock size={16} className="text-muted me-2" />
                                                        <span>{sector.maxDriveTimeMinutes || 30} min</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getStatusColor(sector.isActive)}`}>
                                                        {getStatusText(sector.isActive)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {new Date(sector.createdAt).toLocaleDateString()}
                                                    </small>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Link
                                                            to={`/inventory/v1.2/sectors/${sector._id}`}
                                                            className="btn btn-outline-info btn-sm"
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </Link>                                                        
                                                        <button
                                                            onClick={() => handleDeleteClick(sector)}
                                                            className="btn btn-outline-danger btn-sm"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <Layers size={64} className="text-muted mb-3" />
                                <h5 className="text-muted">No sectors found</h5>
                                <p className="text-muted mb-4">
                                    {searchTerm || statusFilter !== 'all' || serviceableAreaFilter !== 'all'
                                        ? 'Try adjusting your search filters'
                                        : 'Create your first sector to get started'
                                    }
                                </p>
                                {(!searchTerm && statusFilter === 'all' && serviceableAreaFilter === 'all') && (
                                    <Link
                                        to="/inventory/v1.2/sectors/new"
                                        className="btn btn-primary"
                                    >
                                        <Plus size={16} className="me-2" />
                                        Add First Sector
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title">
                                    <AlertTriangle size={20} className="text-danger me-2" />
                                    Confirm Deletion
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={cancelDelete}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Are you sure you want to delete the sector{' '}
                                    <strong>"{sectorToDelete?.name}"</strong>?
                                </p>
                                <div className="alert alert-warning mt-3 mb-0">
                                    <small>
                                        <strong>Warning:</strong> This action cannot be undone and may affect 
                                        associated operations and delivery routes.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer border-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Sector
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

Sectors.propTypes = {
    getSectors: PropTypes.func.isRequired,
    deleteSector: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    sectors: PropTypes.array,
    serviceableAreas: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    sectors: state.sector.sectors,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    loading: state.sector.loading
});

export default connect(mapStateToProps, {
    getSectors,
    deleteSector,
    getServiceableAreas
})(Sectors);
